import { Injectable, EventEmitter } from '@angular/core';
import { SharedService } from '../shared.service';
import * as moment from 'moment';
import { WindowRefService } from 'app/app-window.service';
import { AppHttpService } from 'app/app-http.service';

@Injectable()
export class CartService {
  private discount = 0;
  private couponCode = null;
  private odds = [];
  private products = [];
  private price = 0;
  private priceLotteryOnly = 0;
  private ticketAmount = 0;
  private lottery = null;
  private backPay = 0;
  private totalProfit = 0;
  private dailyPrize = 0;
  private pension = false;
  private pensionRate = 0;
  private productScopeId;
  cartUpdated: EventEmitter<any> = new EventEmitter();

  constructor(
    private sharedService: SharedService,
    private windowRef: WindowRefService,
    private api: AppHttpService
  ) {
    if (this.sharedService.checkLocalStorage('lotteryData')) {
      const savedData = this.sharedService.getLocalStorage('lotteryData');

      this.products = savedData.products;
      this.price = savedData.price;
      this.odds = savedData.odds;
      this.ticketAmount = savedData.ticketAmount;
      this.lottery = savedData.lottery;
      this.pension = savedData.pension;
      this.pensionRate = savedData.pensionRate;

      setTimeout(() => {
        this.triggerCalculation();
      }, 10);
    }
  }

  /*
   * @param {Object} addedProduct
   * @param {Array} odds
   *
   * Adds new product or updates product.amount (ticket amount)
   * of products array.
   *
   * Triggers calculation of price and total ticket amount
   * after a product is updated or added.
   *
   * Triggers also cart emitter to update all components
   * which rely on this data. (e.g. summary box)
   */
  addProduct(addedProduct, odds) {
    /*
     * Checks if products {Array} already contains the new product (@param addedProduct)
     * If not it adds the new products.
     * If it's already in the array it just updates the amount of
     * the selected product (how many tickets of this product were selected).
     */
    const newProduct = this.products.filter(
      this.filterByProductId.bind(this, addedProduct)
    )[0];

    if (!newProduct) {
      this.products.push(addedProduct);
    } else {
      this.products[this.products.indexOf(newProduct)].amount =
        addedProduct.amount;
    }

    if (parseInt(addedProduct.id, 10) === 25) {
      this.pension = true;
    }

    this.odds = odds;

    this.setJokerType();
    this.triggerCalculation();
  }

  /*
   * @param {Object} product
   * @param {Array} odds
   *
   * removes passed product from
   * products.
   */
  removeProduct(product, odds) {
    const selectedProduct = this.products.find(
      (item) => item.id === product.id
    );
    if (this.products.indexOf(selectedProduct) > -1) {
      this.products.splice(this.products.indexOf(selectedProduct), 1);
      this.odds = odds;

      if (parseInt(selectedProduct.id, 10) === 25) {
        this.pension = false;
      }
    }
    this.sharedService.removeFromSessionStorage('jokerType');
    this.triggerCalculation();
  }

  filterProductsByScopeId(productScopeId) {
    if (productScopeId === null) {
      const filteredProducts = this.products.filter(
        (product) => product.category === 'Klassenlotterie'
      );
      this.products = filteredProducts;
    } else {
      const filteredProducts = this.products.filter(
        (product) => product.id === productScopeId.toString()
      );
      this.products = filteredProducts;
    }

    this.triggerCalculation();
  }

  /*
   * @param {Number} discount
   * @param {String} couponCode
   *
   * sets discount amount (e.g. 10 for 10 €) and discount code (e.g. 12345)
   * calls emit function to update
   * all instances.
   */
  setDiscount(discount, couponCode) {
    this.discount = discount;
    this.couponCode = couponCode;
    this.emitData();
  }

  setLottery(lottery) {
    this.lottery = lottery;
    this.emitData();
  }

  setProductScope(productScopeId: Number) {
    this.productScopeId = productScopeId;
  }

  setTotalProfit(totalProfit: number) {
    this.totalProfit = totalProfit;
  }

  setDailyPrize(dailyPrize: number) {
    this.dailyPrize = dailyPrize;
  }

  /*
   * @return {Number} price
   *
   * returns the total price of
   * all tickets.
   * Considers discount set.
   */
  getPrice() {
    return this.discount === 0 ? this.price : this.price - this.discount;
  }

  getStake() {
    return this.price;
  }

  getProductScopeId(): Number {
    return this.productScopeId;
  }

  getLotteryOnlyPrice(): number {
    return this.priceLotteryOnly;
  }

  getTotalPrice() {
    return this.discount === 0
      ? this.price + this.getServiceFee()
      : this.price - this.discount + this.getServiceFee();
  }

  getTotalPriceWithoutDiscount() {
    return this.price + this.getServiceFee();
  }

  /*
   * @return {Number} ticketAmount
   *
   * returns the total amount of
   * tickets selected
   */
  getTicketAmount() {
    return this.ticketAmount;
  }

  /*
   * @return {Number} lotteryTicketAmount
   *
   * returns the amount of selected
   * lottery tickets WITHOUT joker tickets.
   */
  getLotteryTicketAmount() {
    // Find the selected product with category Joker
    const dreamJoker = this.products.find((product) => product.id === '17');
    const euroJoker = this.products.find((product) => product.id === '16');
    const extraJoker = this.products.find((product) => product.id === '51');
    const extraJokerPlus = this.products.find((product) => product.id === '52');
    const pensionJoker = this.products.find((product) => product.id === '25');

    // Since we don't want to calculate the selected joker amount to the
    // selected tickets in case of calculating the odds, we need to get the
    // amount of selected joker products...
    const jokerTicketAmount =
      euroJoker && dreamJoker
        ? dreamJoker.amount + euroJoker.amount
        : dreamJoker
        ? dreamJoker.amount
        : euroJoker
        ? euroJoker.amount
        : pensionJoker
        ? pensionJoker.amount
        : extraJoker
        ? extraJoker.amount
        : extraJokerPlus
        ? extraJokerPlus.amount
        : 0;

    const extraJokerAmount =
      extraJoker && extraJokerPlus
        ? extraJoker.amount + extraJokerPlus.amount
        : extraJoker
        ? extraJoker.amount
        : extraJokerPlus
        ? extraJokerPlus.amount
        : 0;

    // ... and then substract the joker amount from the total tickets.
    const lotteryTicketAmount =
      this.ticketAmount - jokerTicketAmount - extraJokerAmount;

    return lotteryTicketAmount > 0 ? lotteryTicketAmount : 0;
  }

  /*
   * @return {Array} products
   *
   * returns all products which
   * have at least one ticket selected
   * by the user.
   */
  getProducts() {
    // Because of how the codebase grew historically, the products
    // quantity is stored in "amount" and could also be a string.
    // Since this is causing issues with MAPP tracking we put it in
    // quantity and make sure it's an INT.
    // Totally messed up, but I don't want to touch the amount property
    // and have the risk to create bugs all over the app.
    const productsWithQuantity = this.products.map((product) => ({
      ...product,
      quantity: parseInt(product.amount, 10)
    }));

    return productsWithQuantity;
  }

  getProductsForGTMTracking() {
    return this.products.map((product) => ({
      item_id: product.id,
      item_name: product.name,
      coupon: this.couponCode,
      discount: this.discount,
      item_brand: this.sharedService.getEnvironment().toUpperCase(),
      item_category: product.category,
      price: product.price,
      quantity: parseInt(product.amount, 10)
    }));
  }

  getServiceFee() {
    if (this.lottery && this.lottery.serviceFee) {
      return this.lottery.serviceFee;
    } else {
      return 0;
    }
  }

  getBackPay() {
    return this.backPay;
  }

  getLotteryStyle() {
    return this.pension;
  }

  /**
   *
   * @param products
   * @param productScopeID
   * @param isSkl
   *
   * Returns an array with all products to display in the product selection.
   *
   *
   */
  getProductsWithAmount(products, productScopeID = undefined, isSkl = false) {
    const allProducts = products.products;
    const productsWithAmount = [];

    // Loop through all products
    for (let i = 0; i < allProducts.length; i++) {
      const existingProduct = this.getProductById(allProducts[i].id)[0];
      const newProduct = allProducts[i];

      if (existingProduct && allProducts[i].id === existingProduct.id) {
        newProduct.amount = existingProduct.amount;
      } else {
        newProduct.amount = 0;
      }

      // When a product scope ID is set, only show the product with this ID.
      if (!productScopeID && !isSkl) {
        productsWithAmount.push(newProduct);
      } else if (!productScopeID && isSkl) {
        // When SKL then only show the none joker products (without ID 16 / 17)
        if (
          parseInt(newProduct.id, 10) !== 16 &&
          parseInt(newProduct.id, 10) !== 17
        ) {
          productsWithAmount.push(newProduct);
        }
      } else {
        // When none productScopeID is set, show all products.
        if (parseInt(newProduct.id, 10) === parseInt(productScopeID, 10)) {
          productsWithAmount.push(newProduct);
        }
      }
    }

    products.products = productsWithAmount;

    return products;
  }

  /*
   * @param {Number} productId
   * @return {Array} product
   *
   * returns an array with only
   * one item.
   * Gets the product by its product ID
   *
   */
  getProductById(productId) {
    return this.products.filter((product) => {
      return product.id === productId;
    });
  }

  /*
   * @return {Array} product
   *
   * returns an Array with all products selected.
   * But the products are reduced to only its amount
   * and ID.
   * This is how the API expects the order data.
   */
  getProductsForOrder() {
    return this.products.map(function (product) {
      return {
        quantity: parseInt(product.amount, 10),
        id: product.id
      };
    });
  }

  getLotteryCategory() {
    return this.products[0].category;
  }

  getLotteryNumber() {
    return this.lottery.number;
  }

  getLottery() {
    return this.lottery;
  }

  getDiscount() {
    return this.discount;
  }

  getCouponCode() {
    return this.couponCode;
  }

  getPensionRate() {
    return this.pensionRate;
  }

  /*
   *  @return {Number} maxGain
   *
   *  Returns maxGain value of the selected
   *  product with the highest maxGain.
   */
  getMaxGain() {
    return this.products.length > 0 ? this.calculateMaxGain() : 0;
  }

  getTotalProfit(): number {
    return this.totalProfit;
  }

  getDailyPrize(): number {
    return this.dailyPrize;
  }

  /**
   * Depending on the selected product, calculate the max gain
   */
  private calculateMaxGain(): number {
    const maxGains = [];
    this.products.map((product) => {
      if (product.id === '13') {
        // Maxgain of 1.6 Mio (16.000.000 are set in DB)
        maxGains.push(product.maxGain / 10);
      } else if (product.id === '14') {
        // Maxgain of 1.6 Mio multiplied by amount (16.000.000 are set in DB)
        maxGains.push((product.maxGain / 10) * product.amount);
      } else {
        // Maxgain of 1.6 Mio (16.000.000 are set in DB)
        maxGains.push(product.maxGain);
      }
    });

    // Always return the highest maxgain from the selected products.
    return Math.max.apply(Math, maxGains);
  }

  /*
   *  @param {Object} product
   *  @return {Number} amount
   *
   *  Returns the (selected) amount of the passed product
   *  to highlight this element as active.
   */
  getActive(product) {
    const selectedProduct = this.getProductById(product.id);
    return this.products.length > 0 && selectedProduct[0]
      ? selectedProduct[0].amount
      : 0;
  }

  private triggerCalculation() {
    this.calculatePrice();
    this.calculateLotteryOnlyPrice();
    this.calculateTotalTicketAmount();
    this.calculatePensionRate();
    this.calculateBackPay();
    this.emitData();
  }

  /*
   * Update event emitter with new values
   * This "talks" to every component which
   * has subscribed this emitter.
   */
  private emitData() {
    const data = {
      price: this.getPrice(),
      priceLotteryOnly: this.getLotteryOnlyPrice(),
      stake: this.getStake(),
      defaultTotalPrice: this.getTotalPriceWithoutDiscount(),
      maxGain: this.getMaxGain(),
      ticketAmount: this.getTicketAmount(),
      odds: this.calculateOdds(),
      products: this.getProducts(),
      lottery: this.getLottery(),
      couponCode: this.couponCode,
      discount: this.discount,
      backPay: this.backPay,
      pension: this.pension,
      pensionRate: this.getPensionRate()
    };

    this.cartUpdated.emit(data);

    data.odds = this.odds;
    this.sharedService.setLocalStorage('lotteryData', data);

    this.updateBasketIcon();
    this.validateCart();
  }

  /*
   * @return {Boolean}
   *
   * Returns true or false depending of
   * this.products already containing the newly
   * added procuts.
   *
   * ref: this.addProducts()
   */
  private filterByProductId(addedProduct, product) {
    return addedProduct.id === product.id;
  }

  /*
   * @return {Number} odds
   *
   * Calculates and returns the odds
   * by using the amount of selected tickets
   * as index of the odds array.
   */
  private calculateOdds() {
    // Get the "Lottery with same numbers" product, since it has a special odd calculation
    const sameNumbersProducts = this.products.find(
      (product) => product.id === '14'
    );

    // Check if "lottery with same number" is the only selected product. If yes only use the first
    // item of the odds array.
    if (this.products.length === 1 && this.products[0].id === '14') {
      return this.odds[0];
    } else if (this.products.length > 1 && sameNumbersProducts) {
      // If multiple products are selected ignore the amount of selected "lottery with same number" and
      // see it only as 1
      return this.getLotteryTicketAmount() < this.odds.length
        ? this.odds[this.getLotteryTicketAmount() - sameNumbersProducts.amount]
        : 100;
    } else if (this.products.length > 0) {
      // Use the normal calculation
      return this.getLotteryTicketAmount() < this.odds.length
        ? this.odds[this.getLotteryTicketAmount() - 1]
        : 100;
    } else {
      return 0;
    }
  }

  /*
   * @return {Number} price
   *
   * Calculates the total price of all selected
   * tickets.
   */
  private calculatePrice() {
    let price = 0;

    for (let i = 0; i < this.products.length; i++) {
      price += this.products[i].price * this.products[i].amount;
    }

    this.price = price;
  }

  private calculateLotteryOnlyPrice() {
    let price = 0;
    const products = this.products.filter(
      (product) => product.category === 'Klassenlotterie'
    );

    for (let i = 0; i < products.length; i++) {
      price += products[i].price * products[i].amount;
    }

    this.priceLotteryOnly = price;
  }

  private calculateBackPay() {
    const productData = this.products.map((product) => ({
      id: product.id,
      quantity: parseInt(product.amount, 10)
    }));

    if (this.products.length > 0) {
      this.api.validateAfterPay(productData).subscribe((response) => {
        const backPay = response.reduce(
          (a, b) => a + (b['afterPayAmount'] || 0),
          0
        );
        this.backPay = backPay;
        this.emitData();
      });
    } else {
      this.backPay = 0;
    }
  }

  private calculatePensionRate() {
    this.pensionRate =
      this.pension && this.products.length > 0
        ? this.getProductById('25')[0].amount *
          this.getProductById('25')[0].price
        : 0;
  }

  private validateCart() {
    if (this.getTicketAmount() > 0) {
      this.sharedService.validateStep('step1');
    } else {
      this.sharedService.invalidStep('step1');
    }
  }

  /*
   * @return {Number} ticketAmount
   *
   * Calculates the total amount of all selected
   * tickets.
   */
  private calculateTotalTicketAmount() {
    let ticketAmount = 0;

    for (let i = 0; i < this.products.length; i++) {
      ticketAmount += this.products[i].amount;
    }

    this.ticketAmount = ticketAmount;
  }

  /**
   * This method is updating the cart icon in the new website
   * with the selected product amount.
   * The basket object is added to the window global.
   *
   */
  private updateBasketIcon() {
    const basketObj = this.windowRef.nativeWindow.basket;
    if (this.windowRef.nativeWindow.basket) {
      const ticketAmount = this.getTicketAmount();
      basketObj.updateBasket(ticketAmount);
    }
  }

  private setJokerType(): void {
    const jokerType = this.sharedService.getJokerType();

    if (jokerType) {
      this.sharedService.setSessionStorage(
        'jokerType',
        this.sharedService.getJokerType()
      );
    }
  }
}
